import { Component, ElementRef, ViewChild } from "@angular/core";
import {
  ActiveStatus,
  ActiveStatusEnum,
  ManufacturerImage,
  ManufacturerProductTypeConfig,
  ManufacturerProductTypeConfigAddon,
} from "app/model/entities/entity-model";
import { UtilFns } from "app/shared/util-fns";
import { ProductTypeFinderDialogComponent } from "./product-type-finder-dialog.component";
import { GridReadyEvent } from "@ag-grid-community/core";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { AddonFinderDialogComponent } from "app/shared/addon-finder-dialog.component";
import { AgStyleFns } from "app/shared/ag-style-fns";
import { EntityFinderDialog } from "app/shared/entity-finder.dialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { DomainService } from "app/shared/domain.service";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";

export interface EditManufacturerProductTypeConfigDialogParams extends DialogParams {
  // empty means to add one
  manufacturerProductTypeConfig?: ManufacturerProductTypeConfig;
}

@Component({
  templateUrl: "./edit-manufacturer-product-type-config-dialog.component.html",
  standalone: false,
})
export class EditManufacturerProductTypeConfigDialogComponent extends DialogBaseComponent<EditManufacturerProductTypeConfigDialogParams> {
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  mptConfig: ManufacturerProductTypeConfig;
  mptcAddonGridOptions: GridOptionsExt;
  mptcAddons: ManufacturerProductTypeConfigAddon[];

  activeStatuses: ActiveStatus[];

  isAdding: boolean;
  showAllErrors = false;

  constructor(public config: DynamicDialogConfig<any>, public dialogRef: DynamicDialogRef, public domainService: DomainService) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: "60vw",
      height: "70vh",
    });
    this.prepare();
  }

  async prepare() {
    this.mptConfig = this.params.manufacturerProductTypeConfig;
    if (this.mptConfig == null) {
      this.isAdding = true;
      this.mptConfig = this.createMptConfigAsUnchanged();
    }
    this.mptcAddons = this.mptConfig.manufacturerProductTypeConfigAddons;

    let title = "Manufacturer Product Type Configuration";
    title = this.isAdding ? "Add  " + title : "Edit " + title;
    this.config.header = title;

    this.activeStatuses = this.dbQueryService.getAllCached(ActiveStatus);

    this.mptcAddonGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAddonGridReady,
      rowModelType: "clientSide",
    });

    this.isPageReady = true;

    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createMptConfigAsUnchanged() {
    const mptConfig = this.uow.createEntity(ManufacturerProductTypeConfig, {
      activeStatusId: ActiveStatusEnum.Active,
    });
    mptConfig.entityAspect.setUnchanged();
    // Don't call validateEntity() here - it would show errors before user input
    return mptConfig;
  }

  onAddonGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "Name and Location",
        field: "addon.nameAndLocation",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        headerName: "Station",
        field: "addon.addonStation.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Instructions",
        field: "addon.instructions",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
      },
      {
        headerName: "Additional Info",
        field: "additionalInfo",
        filter: "agTextColumnFilter",
        ...AgFns.flexWidth(200),
        editable: this.isAdditionalInfoEditable,
        cellClass: (p) => this.isAdditionalInfoEditable(p) ? "cell-editable" : "",
      },
      // Not yet supported.
      {
        ...AgFns.createButtonProps("", this.detachAddon.bind(this), {
          label: "Detach",
        }),
        ...AgFns.fixWidth(100),
      },
    ];
    const sortModel = [{ colId: "nameAndLocation", sort: "asc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);

    AgFns.applyGridRouteParams(this.mptcAddonGridOptions);
  }

  isAdditionalInfoEditable(p: any) {
    return p.data.addon.needsAdditionalInfo;
  }

  getPngDialogService() {
    return this.domainService.pngDialogService;
  }

  async selectProductType() {
    const r = await EntityFinderDialog.open(ProductTypeFinderDialogComponent, this, {});
    if (r.length > 0) {
      this.mptConfig.productTypeId = r[0].id;
    }
  }

  async attachAddon() {
    const addons = await EntityFinderDialog.open(AddonFinderDialogComponent, this, {
      accountId: null,
    });
    if (addons.length == 0) {
      return;
    }
    const mptConfigAddon = this.uow.createEntity(ManufacturerProductTypeConfigAddon, {
      addonId: addons[0].id,
      manufacturerProductTypeConfigId: this.mptConfig.id,
    });
    AgFns.refreshGrid(this.mptcAddonGridOptions, this.mptcAddons);
  }

  detachAddon(mptcAddon: ManufacturerProductTypeConfigAddon) {
    const aspect = mptcAddon.entityAspect;
    if (aspect.entityState.isAdded()) {
      aspect.setDetached();
    } else {
      aspect.setDeleted();
    }
    AgFns.refreshGrid(this.mptcAddonGridOptions, this.mptcAddons);
  }

  async ok() {
    const ok = await this.save();
    if (!ok) {
      return;
    }
    this.dialogRef.close(true);
  }

  async save() {
    // Show all validation errors when user tries to save
    this.showAllErrors = true;

    if (this.isAdding) {
      this.mptConfig.entityAspect.setAdded();
    }

    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }

    try {
      const sr = await this.dbSaveService.saveChanges();
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    // Check that a Product Type has been selected
    if (!this.mptConfig.productTypeId) {
      await this.dialogService.showOkMessage("Missing Product Type", "Please select a Product Type before saving.");
      return false;
    }

    let matchingEntity = await this.dbQueryService.getMatchingManufacturerProductTypeConfig(this.mptConfig);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage("Duplicate Name", `Cannot save because this config's name is a duplicate of an existing name`);
      return false;
    }

    const ea = this.mptConfig.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }

    return true;
  }

  async cancel() {
    this.isPageReady = false;
    if (this.isAdding) {
      this.mptConfig.entityAspect.setDetached();
    }

    this.uow.rollback();

    this.dialogRef.close(false);
  }

  /** Create the join entity between Image and parent entity */
  createImageJoinEntity(event: { imageId: number; isPrimary: boolean }): void {
    this.dbSaveService.uow.createEntity(ManufacturerImage, {
      manufacturerId: this.mptConfig.id,
      imageId: event.imageId,
      isPrimary: event.isPrimary,
    });
  }
}
